import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from 'react-form';
import moment from 'moment-timezone';
import hash from 'object-hash';
import * as queryString from 'query-string';

import Select from '../../components/Form/Select';
import PlayerInvite from '../../components/PlayerInvite';
import PlayerInviteModal from '../../components/Modals/PlayerInviteModal';
import GenericPopup from '../../components/PopUps/GenericPopup';
import Img from '../../components/Img';

import { Input, DateTimePicker, Location, UTRRangeSliderSmall, CheckBox } from '../../components/Form';
import { isLoginPopUpNeeded, userParams, titles } from '../../components/PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { saveEvent } from '../../actions/event-actions';
import { hideSplash } from '../../actions/splash-actions';
import { setPlayerId } from '../../actions/profile-actions';
import { required } from '../../utils/form-validation';
import { showLoginPopUp } from '../../actions/login-popup-actions';
import {
    dateFormat,
    durationOptions,
    getPaidHitButtonTextForState,
    getEventEndDate,
    getNewEmailInvites,
    getNewMemberInvites,
    getPaidHitPopupContentForState,
    mapMembers,
    mapEmailInvites,
    permissionTypes,
    validateDate,
    mapPlayer,
    mapOrganizer,
} from '../../utils/createPlay';
import { ClubTierTypes } from '../../utils/clubs';
import { EventTypes, getTimeZones } from '../../utils/events';
import { DefaultTimeZone } from '../../utils/eventConstants';
import config from '../../../config';
import styles from './createPaidHitPage.scss';
import { ratingTypes } from '../../utils/constants';
import { useStatsig } from '../../components/hooks/useStatsig';

const termsCopy = <span className={'text-medium text-weight-normal'}>I agree to implement local health & safety protocols and to the paid hits <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer">terms of service</a>.</span>;
const utrRangeHoverText = `Player’s ${ratingTypes.tennis} Rating is the desired range that a Paid Hitter would like to hit with. The wider the range, the more players can feel comfortable booking your Paid Hits session.`;

const CreatePaidHitPage = (props) => {
    const auth = useSelector((state) => state.auth);
    const events = useSelector((state) => state.events);
    const profile = useSelector((state) => state.profile);
    const coreUITimeZones = useSelector((state) => state.coreUI.types.timeZones);
    const history = useHistory();
    const dispatch = useDispatch();
    const location = useLocation();

    const { isEditingEvent, match, isDuplicating } = props;
    const { user } = auth;
    const { isSaving } = events.currentEvent || {};
    const { initializeStatsigUser } = useStatsig();
    const organizer = mapOrganizer(user);
    const { defaultPaidHitPrice } = user?.claimedPlayer?.playerProfileInformation || 50;
    const popupContent = getPaidHitPopupContentForState(isEditingEvent);
    const eventName = `${organizer?.source?.displayName} Paid Hit`;
    const momentTimeZone = moment.tz.guess();
    const currentTimeZone = momentTimeZone ? coreUITimeZones.find(timeZone => timeZone.value?.toLowerCase() === momentTimeZone.toLowerCase()) : null;
    const defaultTimeZone = currentTimeZone ? parseInt(currentTimeZone.id) : DefaultTimeZone;

    const [isFinishedLoading, setIsFinishedLoading] = useState(false);
    const [hasCheckedTerms, setHasCheckedTerms] = useState(false);
    const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
    const [savedEventId, setSavedEventId] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [previousInvitees, setPreviousInvitees] = useState({ members: [], nonMembers: [] });
    const [originalGroupMembers, setOriginalGroupMembers] = useState([]);
    const [removedGroupMemberId, setRemovedGroupMemberId] = useState('');
    const [timezone, setTimezone] = useState(defaultTimeZone);
    const [showTimezone, setShowTimezone] = useState(false);
    const [pageTitle, setPageTitle] = useState('Create');
    const [eventData, setEventData] = useState({
        name: eventName,
        eventPermissionType: 1,
        details: '',
        eventSchedule: {
            eventStartUtc: moment(),
            timezone: defaultTimeZone,
        },
        eventDivisions: [{
            entryFee: defaultPaidHitPrice || 50,
        }],

        eventLocations: [{
            location: '',
            eventLocationType: '1',
        }],
        eventType: EventTypes.PAID_HIT,
        utrRange: [1, 14],
        eventDuration: 1,
        memberInvites: [],
        emailInvites: [],
        termsChecked: false,
    });

    const initForm = () => {
        const { eventId } = match?.params || {};
        const currentEvent = events?.currentEvent?.data;
        if (isEditingEvent && currentEvent.id === parseInt(eventId)) {
            const { eventSchedule, eventPermissionType, eventLocations, eventDivisions, invites } = currentEvent;
            const { minUtr, maxUtr, entryFee } = eventDivisions[0];
            const utrRange = [minUtr, maxUtr];
            const timeZone = eventSchedule?.timeZone?.id ?? DefaultTimeZone;
            const timeZoneObj = coreUITimeZones.find(tz => { return tz.id === parseInt(timeZone); });
            const startDate = moment.utc(eventSchedule.eventStartUtc).tz(timeZoneObj.value);
            const endDate = moment.utc(eventSchedule.eventEndUtc).tz(timeZoneObj.value);
            const duration = endDate.diff(startDate, 'hours', true);
            const permissionTypeId = eventPermissionType.id;
            let memberInvites = [];
            let emailInvites = [];
            if (invites && invites.members.length > 0) {
                memberInvites = mapMembers(invites.members);
            }
            if (invites && invites.nonMembers.length > 0) {
                emailInvites = mapEmailInvites(invites.nonMembers);
            }
            const location = eventLocations[0].googleFormattedName;
            const notes = currentEvent.details;
            setPageTitle(isDuplicating ? 'Duplicate' : 'Edit');
            setPreviousInvitees({ members: memberInvites, nonMembers: emailInvites });
            setTimezone(timeZone);
            setEventData({
                name: eventName,
                eventPermissionType: permissionTypeId,
                details: notes,
                eventSchedule: {
                    eventStartUtc: isDuplicating ? null : startDate,
                    timezone: timeZone,
                },
                eventDivisions: [{
                    entryFee,
                }],

                eventLocations: [{
                    location,
                    eventLocationType: '1',
                }],
                eventType: EventTypes.PAID_HIT,
                utrRange,
                eventDuration: duration,
                memberInvites,
                emailInvites,
                termsChecked: false,
            });
            setIsFinishedLoading(true);
            if (isDuplicating) {
                dispatch(hideSplash());
            }
        } else {
            setIsFinishedLoading(true);
        }
    };

    useEffect(() => {
        // new events and in case event is already loaded in redux
        initForm();
        showLoginModal();
    }, []);

    const showLoginModal = () => {
        const { shared } = queryString.parse(location.search);
        const userParamsNeeded = [userParams.CLAIMED_PROFILE];
        const isLoginPopupNeeded = isLoginPopUpNeeded(auth, userParamsNeeded);
        const popupData = {
            userParamsNeeded,
            titles: titles.EVENT_REGISTER,
            type: 'register',
        };
        if (isLoginPopupNeeded || (shared && !user)) {
            dispatch(showLoginPopUp(popupData));
        }
    };

    useEffect(() => {
        if (user) {
            initializeStatsigUser();
        }
    }, [user]);

    useEffect(() => {
        const { invitingPlayer } = queryString.parse(location.search);
        if (invitingPlayer) {
            if (!profile.isLoading && !profile.isProfileLoaded) {
                dispatch(setPlayerId(invitingPlayer, false));
            }
            if (profile.isProfileLoaded) {
                const inviteeMember = mapPlayer(profile.data);
                setEventData({ ...eventData, memberInvites: [...eventData.memberInvites, inviteeMember] });
            }
        }
    }, [profile]);

    useEffect(() => {
        // event edit - when weload another event by id
        initForm();
    }, [events?.currentEvent?.data]);

    const handleOnSubmitForm = async (formValues) => {
        const event = { ...formValues };
        const [minUtr, maxUtr] = formValues.utrRange;
        const { emailInvites } = eventData;
        const memberInvites = eventData.memberInvites.map(member => { return { memberId: member.source.memberId }; });
        const invitees = [...memberInvites, ...emailInvites];
        const paidHitterClub = user?.clubMemberships.find(membership => membership.tierTypeId === ClubTierTypes.PAID_HIT);
        const permissionType = parseInt(event.eventPermissionType);
        const eventDirector = [{ memberId: user?.id, eventDirectorRole: 2 }];
        const endDate = getEventEndDate(event.eventSchedule.eventStartUtc, event.eventDuration);
        const newEventDivision = {
            ...event.eventDivisions[0],
            minUtr,
            maxUtr,
        };
        event.eventSchedule.eventEndUtc = endDate;
        event.eventSchedule.timezone = timezone;
        event.eventDirectors = eventDirector;
        event.club = paidHitterClub.clubId;
        event.eventDivisions = [newEventDivision];
        event.eventPermissionType = permissionType;
        delete event.utrRange;
        delete event.eventDuration;
        delete event.termsChecked;
        delete event.emailInvites;
        delete event.memberInvites;

        if (isEditingEvent) {
            const { eventId } = match?.params || {};
            const newMemberInvites = getNewMemberInvites(previousInvitees.members, memberInvites);
            const newEmailInvites = getNewEmailInvites(previousInvitees.nonMembers, emailInvites);
            event.id = eventId;
            event.playersList = [...newMemberInvites, ...newEmailInvites];
        } else {
            event.playersList = invitees;
        }

        const savedEvent = await dispatch(saveEvent(event));
        if (savedEvent) {
            setSavedEventId(savedEvent.id);
            setShowSuccessPopup(true);
        }
    };

    const handleOnDateChange = (date) => {
        setEventData({ ...eventData, eventSchedule: { ...eventData.eventSchedule, eventStartUtc: date } });
    };

    const handleOnRemovePlayerFromInvite = (playerId) => {
        const updatedPlayers = eventData.memberInvites.filter(player => player.source.id !== playerId);
        const isGroupMember = originalGroupMembers.some(member => member.source.id === playerId);

        if (isGroupMember) {
            setRemovedGroupMemberId(playerId);
        }
        setEventData({ ...eventData, memberInvites: updatedPlayers });
    };

    const handleOnRemoveEmailFromInvite = (email) => {
        const updatedPlayers = eventData.emailInvites.filter(player => player.email !== email);
        setEventData({ ...eventData, emailInvites: updatedPlayers });
    };

    const handleOnInvitePlayerClicked = () => {
        setIsInviteModalVisible(true);
    };

    const handleOnRequestModalClose = () => {
        setIsInviteModalVisible(false);
    };

    const handleOnApplyClicked = (memberInvites, emailInvites) => {
        setEventData({ ...eventData, memberInvites, emailInvites });
    };

    const handleOnPopupRequestClose = () => {
        const splashMessage = isEditingEvent ? 'Your event has been updated!' : 'Your event has been created!';
        setShowSuccessPopup(false);
        history.push({
            pathname: `/events/${savedEventId}`,
            state: { adminSplash: splashMessage },
        });
    };

    const handleOnTermsCheckboxChange = (isChecked) => {
        setHasCheckedTerms(isChecked);
    };

    const handleOnCloseEditPage = () => {
        const { eventId } = match.params;
        if (eventId) {
            history.push(`/events/${eventId}`);
        } else {
            history.goBack();
        }
    };

    const handleOnGroupMembersSet = (members) => {
        setOriginalGroupMembers(members);
    };

    const toggleTimezone = (show, val) => {
        setShowTimezone(show);
        if (val) {
            setTimezone(parseInt(val));
            const timeZoneNew = coreUITimeZones.find(tz => { return tz.id === parseInt(val); });
            const newEventStartDate = eventData.eventSchedule.eventStartUtc.tz(timeZoneNew.value);
            setEventData({ ...eventData, eventSchedule: { ...eventData.eventSchedule, eventStartUtc: newEventStartDate } });
        }
    };
    const timeZones = getTimeZones(coreUITimeZones);

    // used as a key to force re-render and re-init the form using the new defaultValues
    const formDataHash = hash(eventData);

    return (
        <>
            <div className="navbar navbar-expand-md navbar-light mt0 sticky-top">
                <div className="container" style={{ position: 'relative' }}>
                    <div className="row w-100">
                        <div className="col d-flex aic">
                            <div className="close-area" onClick={handleOnCloseEditPage} data-testid="create-paid-hit.page.close-btn">
                                <Img className="close-icon" src="/img/icons/close-icon-lg.svg" data-testid="create-paid-hit.page.close-icon" />
                            </div>
                            <div className="d-inline d-md-none title-span text-medium text-uppercase text-dark text-center" data-testid="create-paid-hit.page.mobile-title">
                                {pageTitle} Paid Hit
                            </div>
                        </div>
                        <div className="col d-none d-md-flex aic jcc">
                            <div className="title-span text-medium text-uppercase text-dark text-center" data-testid="create-paid-hit.page.desktop-title">
                                {pageTitle} Paid Hit
                            </div>
                        </div>
                        <div className="col d-flex aic jce" />
                    </div>
                </div>
            </div>
            <div className="container">
                <div className={'row justify-content-center mt48'}>
                    {user && (
                        <div className={`${styles.title} col-8`} data-testid="create-paid-hit.page.event-name">{eventName}</div>
                    )}
                    {isFinishedLoading && (
                        <div key={formDataHash}>
                            <Form onSubmit={handleOnSubmitForm} defaultValues={{ ...eventData }}>
                                {(formApi) => {
                                    const { submitForm } = formApi;
                                    return (
                                        <div>
                                            <form onSubmit={submitForm} id="createPaidHitForm" className="mt64" data-testid="create-paid-hit.page.form">
                                                <DateTimePicker
                                                    label="Date and Time"
                                                    id="eventStart"
                                                    field={['eventSchedule', 'eventStartUtc']}
                                                    selected={eventData.eventSchedule.eventStartUtc}
                                                    minDate={moment()}
                                                    maxDate={null}
                                                    handleDateChange={handleOnDateChange}
                                                    hiddenLabel={false}
                                                    dateFormat={dateFormat}
                                                    validate={validateDate}
                                                />
                                                <div className="mb32">
                                                    <p className="text-small"><strong>Timezone: </strong>
                                                        {(timezone ? timeZones.find(tz => tz.id === parseInt(timezone)).label : '')}
                                                        <a
                                                            href="#"
                                                            className="underline"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                toggleTimezone(true);
                                                            }}
                                                        >
                                                            change
                                                        </a>
                                                    </p>
                                                    {showTimezone
                                                        ? (
                                                            <div className="row" display="none">
                                                                <div className="col-md-8">
                                                                    <Select
                                                                        id="timezone"
                                                                        hiddenLabel={true}
                                                                        hiddenDefault={true}
                                                                        field="timeZone"
                                                                        options={timeZones}
                                                                        onChange={(val) => toggleTimezone(false, val)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                </div>
                                                <Select
                                                    id="duration"
                                                    label="Duration"
                                                    field="eventDuration"
                                                    hiddenDefault={true}
                                                    options={durationOptions}
                                                />
                                                <Input
                                                    id="price"
                                                    field={['eventDivisions', 0, 'entryFee']}
                                                    label="Paid Hit Price"
                                                    hiddenLabel={false}
                                                />
                                                <UTRRangeSliderSmall
                                                    id="utrRange"
                                                    label={`Player's ${ratingTypes.tennis} Rating`}
                                                    max={14}
                                                    hoverText={utrRangeHoverText}
                                                    field={'utrRange'}
                                                />
                                                <Select
                                                    id="privacy"
                                                    label="Visibility"
                                                    field="eventPermissionType"
                                                    hiddenDefault={true}
                                                    options={permissionTypes}
                                                />
                                                <div className="mt64">
                                                    <Location
                                                        label="Event Location"
                                                        id="location"
                                                        field={['eventLocations', 0, 'location']}
                                                        inputProps={{ name: 'location' }}
                                                        useDynamicOption={true}
                                                        showMap={false}
                                                        validate={required}
                                                    />
                                                    <Input
                                                        id="details"
                                                        field="details"
                                                        label="Add Note (Optional)"
                                                        hiddenLabel={false}
                                                    />
                                                </div>
                                                <div className="mb40">
                                                    <PlayerInvite
                                                        isEditingEvent={isEditingEvent}
                                                        invitedMembers={eventData.memberInvites}
                                                        invitedEmails={eventData.emailInvites}
                                                        previousInvites={previousInvitees}
                                                        organizer={organizer}
                                                        onRemovePlayer={handleOnRemovePlayerFromInvite}
                                                        onRemoveEmail={handleOnRemoveEmailFromInvite}
                                                        onInvitePlayerPressed={handleOnInvitePlayerClicked}
                                                    />
                                                </div>
                                                <CheckBox
                                                    id="termsCheck"
                                                    field="termsCheck"
                                                    customLabel={termsCopy}
                                                    formGroupCssClass="form-group"
                                                    checked={hasCheckedTerms}
                                                    onChange={handleOnTermsCheckboxChange}
                                                    controlled
                                                />
                                                <button
                                                    type="submit"
                                                    className={`btn btn-primary btn-xl text-uppercase btn-block ${hasCheckedTerms ? null : 'disabled'}`}
                                                    disabled={!hasCheckedTerms || isSaving}
                                                    data-testid="create-paid-hit.page.submit-btn"
                                                >
                                                    {getPaidHitButtonTextForState(isEditingEvent, isSaving, isDuplicating)}
                                                </button>
                                            </form>
                                            <PlayerInviteModal
                                                isVisible={isInviteModalVisible}
                                                invitedMembers={eventData.memberInvites}
                                                invitedEmails={eventData.emailInvites}
                                                removedGroupMemberId={removedGroupMemberId}
                                                onRequestClose={handleOnRequestModalClose}
                                                onApplyClicked={handleOnApplyClicked}
                                                onGroupMembersSet={handleOnGroupMembersSet}
                                            />
                                            {showSuccessPopup && (
                                                <GenericPopup
                                                    title={popupContent.title}
                                                    subtitle={popupContent.subtitle}
                                                    imageSource="/img/icons/checkmark-teal.svg"
                                                    buttonText="OK"
                                                    onRequestClose={handleOnPopupRequestClose}
                                                    onButtonClick={handleOnPopupRequestClose}
                                                    data-testid="modal.create-paid-hit.success"
                                                />
                                            )}
                                        </div>
                                    );
                                }}
                            </Form>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

CreatePaidHitPage.propTypes = {
    isEditingEvent: PropTypes.bool.isRequired,
    isDuplicating: PropTypes.bool.isRequired,
    match: PropTypes.instanceOf(Object).isRequired,
};

export default React.memo(CreatePaidHitPage);
